import { cn } from "../../lib/utils";
import { JANEWAY_KOPF } from "./janeway-kopf";
import { type LucideIcon } from "lucide-react";
import { APP_GLYPHS, FALLBACK_GLYPH } from "./app-glyphs";
import { RegistryGlyph } from "./RegistryGlyph";

export interface AppIconProps {
  /** App-/Modul-id → wählt das Glyph aus APP_GLYPHS. */
  appId?: string;
  /** Direktes Glyph (überschreibt appId-Lookup) — z.B. für Icon-Review-Kandidaten. */
  glyph?: LucideIcon;
  /**
   * lucide-Glyph-Name (PascalCase, z.B. "Building2") — wird dynamisch aufgelöst,
   * wenn `appId` NICHT in APP_GLYPHS steht. So bekommen neue Apps aus der live
   * `infra_apps`-Registry ein Icon OHNE Code-Änderung. Unbekannter Name → Fallback.
   */
  glyphName?: string;
  /**
   * Bildquelle statt Linien-Glyph — fuer Marken, die als Datei vorliegen und
   * sich nicht als Strichzeichnung nachbauen lassen (der Janeway-Kopf im
   * KI-Chat). Das Bild bekommt dieselbe 66-%-Normalisierung wie ein Glyph, sitzt
   * also im selben Raster. Es wird NICHT eingefaerbt: es muss auf hellem wie
   * dunklem Material tragen, weil `--primary` zwischen den Modi kippt.
   */
  image?: string;
  /**
   * Material NICHT an das Farbschema koppeln, sondern beim festen Janeway-Verlauf
   * lassen (#373737 → #131313) — demselben, den `scripts/gen-app-icons.mjs` in die
   * PWA-Icons und Favicons backt. Fuer Marken, die ueberall gleich aussehen sollen,
   * egal welches Farbthema gerade laeuft.
   */
  staticMaterial?: boolean;
  /** Kantenlänge in px. Ignoriert, wenn `fill` gesetzt ist. */
  size?: number;
  /** Füllt den Eltern-Container (h-full w-full) statt fixer px-Größe — für responsive Tiles. */
  fill?: boolean;
  /**
   * Erzwingt die Hairline/Schatten-Variante fuer dunklen Untergrund. Nur setzen,
   * wenn das Icon auf einem Grund sitzt, der NICHT dem Modus folgt (Hub-Grid:
   * immer dunkel). Ohne die Angabe folgt die Kante der `dark`-Klasse — das ist
   * SSR-fest, waehrend ein aus `resolvedTheme` abgeleiteter Wert beim ersten
   * Render noch unbekannt waere und die Kante nach der Hydration umspringen liesse.
   */
  isDark?: boolean;
  /**
   * Nur fuer das App-Logo der eigenen `/install`-Seite: in Labs dieselbe Kachel wie das
   * PWA-Icon, das dort installiert wird — Prod-Kachel mit Glyph in Bernstein („Variante C",
   * Betreiber 2026-11-03). Seitenleiste, App-Switcher und Hub bleiben beim Theme.
   */
  labsMaterial?: boolean;
  className?: string;
}

/**
 * Code-gezeichnetes App-Icon: Squircle-Material mit Verlauf, darin ein
 * einheitlich normalisiertes Linien-Glyph. Ein Material-System → perfekte
 * Grid-Kohärenz, kein Double-Rounding, kein gebackener Raster-Drift.
 *
 * Seit 2026-07-24 folgt das Material dem FARBSCHEMA statt fest #373737→#131313
 * zu sein. Der Verlauf wird per `color-mix` aus `--primary` gebaut: die obere
 * Kante um 14 % aufgehellt, die untere um 22 % abgedunkelt — dieselbe Tiefe wie
 * vorher, nur eben in der Themefarbe. Im Janeway-Theme ist `--primary`
 * hsl(0 0% 9%), der Verlauf landet damit praktisch auf den alten Werten.
 *
 * NICHT betroffen sind die gebackenen Grafiken: PWA-Homescreen-Icon und Favicon
 * bleiben beim Janeway-Material. Ein installiertes PWA-Icon laesst sich zur
 * Laufzeit nicht umfaerben; es an das Farbschema zu koppeln wuerde also nur
 * auseinanderlaufen. Erzeugt werden sie ueber `scripts/gen-app-icons.mjs`.
 */
/**
 * Der feste Janeway-Verlauf, bevor das Material 2026-07-24 an `--primary` gekoppelt
 * wurde. Identisch mit dem in `scripts/gen-app-icons.mjs` gebackenen Verlauf — wer
 * einen der beiden Werte aendert, aendert beide.
 */
const JANEWAY_MATERIAL = "linear-gradient(to bottom right, #373737, #131313)";

export function AppIcon({ appId, glyph, glyphName, image: bildVorgabe, staticMaterial = false, size = 56, fill = false, isDark, labsMaterial = false, className }: AppIconProps) {
  // Priorität: direktes Glyph > id-Lookup (APP_GLYPHS) > dynamischer lucide-Name
  // (neue Apps ohne Code) > Fallback.
  //
  // Der dritte Zweig liegt in `RegistryGlyph` und wird NACHGELADEN. Er brauchte
  // die komplette lucide-Registry (1184 Icons, 434 KB roh), weil der Name erst
  // zur Laufzeit aus der DB kommt — und bezahlt hat das jede Seite jeder App,
  // obwohl die 33 statischen `APP_GLYPHS` ihn fast immer vorher abfangen.
  // Der Hub traegt das Janeway-Zeichen statt eines Glyphs — und zwar auf der festen
  // dunklen Kachel der Marke, nicht im Verlauf des Themes (Betreiber, 2026-10-12):
  // die Marke folgt keinem Farbschema.
  const janewayZeichen = appId === "janeway-hub" && !glyph && !bildVorgabe;
  const image = bildVorgabe ?? (janewayZeichen ? JANEWAY_KOPF : undefined);
  const festesMaterial = staticMaterial || janewayZeichen;
  const statischesGlyph = glyph ?? APP_GLYPHS[appId ?? ""];
  const registryName = !statischesGlyph && glyphName ? glyphName : undefined;
  const Glyph = statischesGlyph ?? (registryName ? undefined : FALLBACK_GLYPH);
  // Das Labs-Icon traegt einen Bernstein-Glyph (`--jw-labs-glyph` setzt `PublicEnvScript` nur in Labs).
  const glyphFarbe = labsMaterial
    ? "text-[color:var(--jw-labs-glyph,var(--primary-foreground))]"
    : "text-primary-foreground";
  return (
    <span
      style={{
        ...(fill ? undefined : { width: size, height: size }),
        // Verlauf aus --primary statt fester Grauwerte. `in oklab` mischt
        // wahrnehmungsrichtig — bei einer bunten Themefarbe kippt der Ton dadurch
        // nicht ins Graue, wie es bei sRGB-Mischung passieren wuerde.
        // `--jw-labs-material` setzt `PublicEnvScript` nur in Labs; gelesen nur mit `labsMaterial`.
        backgroundImage: festesMaterial
          ? JANEWAY_MATERIAL
          : (labsMaterial ? "var(--jw-labs-material, " : "") +
            "linear-gradient(to bottom right," +
            " color-mix(in oklab, var(--primary) 86%, white 14%)," +
            " color-mix(in oklab, var(--primary) 78%, black 22%))" +
            (labsMaterial ? ")" : ""),
      }}
      className={cn(
        "relative flex items-center justify-center overflow-hidden rounded-[22%]",
        fill && "h-full w-full",
        "ring-1",
        isDark === undefined
          ? "ring-black/10 shadow-md dark:ring-white/12 dark:shadow-[0_1px_2px_rgba(0,0,0,0.55)]"
          : isDark
            ? "ring-white/12 shadow-[0_1px_2px_rgba(0,0,0,0.55)]"
            : "ring-black/10 shadow-md",
        className,
      )}
    >
      {/* dezente obere Lichtkante (simuliert die physische Squircle-Wölbung) */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-[22%] bg-gradient-to-b from-white/[0.06] to-transparent" />
      {image ? (
        // 76 statt der 66 % eines Glyphs: ein Linien-Glyph fuellt seine 24er-Box fast
        // ganz aus, eine Bilddatei bringt eigenen Rand mit (der Janeway-Kopf belegt
        // 83 % seiner Breite). Bei 66 % waere das Zeichen sichtbar kleiner als die
        // Glyphs daneben.
        <img src={image} alt="" aria-hidden="true" className="relative h-[76%] w-[76%] object-contain" />
      ) : (
        /* primary-foreground ist die Farbe, die AUF primary lesbar ist — im
           Janeway-Theme praktisch Weiss, also unveraendert. */
        Glyph ? (
          <Glyph aria-hidden="true" strokeWidth={1.75} className={cn("relative h-[66%] w-[66%]", glyphFarbe)} />
        ) : (
          <RegistryGlyph name={registryName!} className={cn("relative h-[66%] w-[66%]", glyphFarbe)} />
        )
      )}
    </span>
  );
}
